import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CompositionLookup,
  CompositionRecord,
  CompositionsApi,
  NewProduct,
  PipelineCheck,
  ProductHistory,
  ProductSummary,
} from './types';

export class CompositionsClient implements CompositionsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  listProducts(): Promise<ProductSummary[]> {
    return this.getJson('/products');
  }

  getProduct(name: string): Promise<ProductHistory> {
    return this.getJson(`/products/${encodeURIComponent(name)}`);
  }

  getBuild(product: string, buildId: number): Promise<CompositionRecord> {
    return this.getJson(
      `/products/${encodeURIComponent(product)}/builds/${buildId}`,
    );
  }

  findComposition(compositionId: string): Promise<CompositionLookup> {
    return this.getJson(`/compositions/${encodeURIComponent(compositionId)}`);
  }

  async checkPipeline(url: string, branch?: string): Promise<PipelineCheck> {
    return (
      await this.request('/pipelines/check', {
        method: 'POST',
        body: { url, branch },
      })
    ).json();
  }

  async addProduct(product: NewProduct): Promise<void> {
    await this.request('/products', { method: 'POST', body: product });
  }

  async removeProduct(name: string): Promise<void> {
    await this.request(`/products/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  async downloadSbom(product: string, buildId: number) {
    const response = await this.get(
      `/products/${encodeURIComponent(product)}/builds/${buildId}/sbom`,
    );
    const disposition = response.headers.get('content-disposition') ?? '';
    const fileName =
      /filename="?([^";]+)"?/.exec(disposition)?.[1] ??
      `sbom-${buildId}.cdx.json`;
    return { blob: await response.blob(), fileName };
  }

  private async getJson<T>(path: string): Promise<T> {
    return (await this.get(path)).json();
  }

  private get(path: string): Promise<Response> {
    return this.request(path);
  }

  private async request(
    path: string,
    options: { method?: string; body?: unknown } = {},
  ): Promise<Response> {
    const baseUrl = await this.discoveryApi.getBaseUrl('compositions');
    const url = `${baseUrl}${path}`;
    const response = options.method
      ? await this.fetchApi.fetch(url, {
          method: options.method,
          headers: { 'Content-Type': 'application/json' },
          body:
            options.body === undefined
              ? undefined
              : JSON.stringify(options.body),
        })
      : await this.fetchApi.fetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      const error = body?.error;
      const message =
        typeof error === 'string'
          ? error
          : error?.message ?? `HTTP ${response.status}`;
      throw new Error(message);
    }
    return response;
  }
}
